import { html, mount } from '../../core/html.js';
import { get, post, del, session } from '../../core/api.js';
import { calendar, toast, withBusy, emptyState, confirmDialog, showError, icon } from '../../core/ui.js';
import { addDays, fmtDateShort, fmtTs } from '../../core/format.js';
import { t } from '../../core/i18n.js';
import { dateField, setDateField } from '../../core/controls.js';
import { workspace } from '../shared/layout.js';
import { navigate } from '../../app.js';

// Himpun tarikh berturutan dengan sumber/nota/tempahan sama menjadi satu julat.
function runs(cal, pick) {
  const out = [];
  for (const d of cal.filter(pick).sort((a, b) => (a.date < b.date ? -1 : 1))) {
    const key = d.booking_id ? `B${d.booking_id}` : `${d.source}|${d.note || ''}`;
    const last = out[out.length - 1];
    if (last && last.key === key && addDays(last.to, 0) === d.date) { last.to = addDays(d.date, 1); continue; }
    out.push({ key, from: d.date, to: addDays(d.date, 1), d });
  }
  return out;
}

async function copy(text) {
  try { await navigator.clipboard.writeText(text); toast(t('hcal.copied')); } catch { window.prompt(t('hcal.copy_manual'), text); }
}

export async function render(main, _p, query) {
  const body = workspace(main, 'host');
  const props = (await get('/api/host/properties')).filter((p) => p.status !== 'DELETED');
  if (!props.length) { mount(body, emptyState(t('hcal.no_property_title'), t('hcal.no_property_text'), html`<a class="btn btn-primary" href="#/host/properties/new">${t('hprops.create')}</a>`)); return; }
  const pid = query.get('property') || props[0].id;
  const today = session.config.today;
  const [cal, ical] = await Promise.all([
    get(`/api/host/properties/${pid}/calendar?from=${today}&to=${addDays(today, 395)}`),
    get(`/api/host/properties/${pid}/ical`),
  ]);
  const marks = new Map(cal.map((d) => [d.date, d.status === 'BLOCKED' ? 'blocked' : 'booked']));
  const bookings = runs(cal, (d) => !!d.booking_id);
  const externals = runs(cal, (d) => d.status === 'BLOCKED' && d.source === 'EXTERNAL');
  const synced = runs(cal, (d) => d.status === 'BLOCKED' && d.source === 'ICAL');
  let picked = [];
  const srcLabel = (r) => (r.d.source === 'ICAL' ? t('hcal.src_ical', { name: r.d.note || '' }) : r.d.note || t('hcal.src_external'));

  mount(body, html`<div class="spread"><h1 style="margin:0">${t('hostnav.calendar')}</h1>
    <select class="input" id="prop" style="width:auto" aria-label="${t('hcal.pick_property')}">${props.map((p) => html`<option value="${p.id}" ${p.id === pid ? 'selected' : ''}>${p.name}</option>`)}</select></div>

    <form class="panel stack" id="ext" style="margin-top:16px">
      <div><h2 style="margin:0">${t('hcal.ext_title')}</h2><p class="small muted" style="margin:4px 0 0">${t('hcal.ext_text')}</p></div>
      <div class="form-grid two">
        <label class="field"><span>${t('booking.check_in')}</span>${dateField('check_in', { id: 'eci', min: today, required: true, placeholder: t('home.add_date') })}</label>
        <label class="field"><span>${t('booking.check_out')}</span>${dateField('check_out', { id: 'eco', min: addDays(today, 1), required: true, placeholder: t('home.add_date') })}</label>
        <label class="field span2"><span>${t('hcal.ext_note')}</span><input class="input" name="note" maxlength="120" placeholder="${t('hcal.ext_note_ph')}"></label>
      </div>
      <button class="btn btn-primary">${t('hcal.ext_save')}</button>
    </form>

    <h2 style="margin-top:24px">${t('hcal.calendar_title')}</h2>
    <p class="muted small">${t('hcal.hint')}</p>
    <div class="legend"><span><i style="background:var(--indigo-tint)"></i>${t('hcal.legend_booked')}</span><span><i style="background:var(--embun)"></i>${t('hcal.legend_blocked')}</span><span><i style="box-shadow:inset 0 0 0 2px var(--padi)"></i>${t('hcal.legend_picked')}</span></div>
    <div class="panel" style="margin-top:12px"><div id="cal"></div></div>
    <div class="row" style="margin-top:12px"><span id="sel" class="small muted">${t('hcal.none_picked')}</span>
      <button class="btn btn-ghost btn-sm" id="block" disabled>${t('hcal.block')}</button><button class="btn btn-ghost btn-sm" id="unblock" disabled>${t('hcal.unblock')}</button></div>

    <h2 style="margin-top:24px">${t('hcal.upcoming_title')}</h2>
    ${bookings.length || externals.length || synced.length ? html`<div class="list">
      ${bookings.map((r) => html`<a class="item" href="#/bookings/${r.d.booking_id}"><span class="status info">${t('hcal.tag_platform')}</span><span class="grow"><b>${fmtDateShort(r.from)} – ${fmtDateShort(r.to)}</b><div class="small muted" translate="no">${r.d.booking_id}</div></span></a>`)}
      ${externals.map((r) => html`<div class="item"><span class="status warn">${t('hcal.tag_external')}</span><span class="grow"><b>${fmtDateShort(r.from)} – ${fmtDateShort(r.to)}</b><div class="small muted" data-ugc>${srcLabel(r)}</div></span><button class="btn btn-ghost btn-sm" data-free="${r.from}|${r.to}">${t('hcal.free')}</button></div>`)}
      ${synced.map((r) => html`<div class="item"><span class="status">${t('hcal.tag_ical')}</span><span class="grow"><b>${fmtDateShort(r.from)} – ${fmtDateShort(r.to)}</b><div class="small muted" data-ugc>${srcLabel(r)}</div></span></div>`)}
    </div>` : html`<p class="muted small">${t('hcal.upcoming_none')}</p>`}

    <section class="panel stack" style="margin-top:24px" id="sync">
      <div><h2 style="margin:0">${t('hcal.sync_title')}</h2><p class="small muted" style="margin:4px 0 0">${t('hcal.sync_text')}</p></div>
      <div class="stack">
        <h3 style="margin:0">${t('hcal.export_title')}</h3>
        <p class="small muted" style="margin:0">${t('hcal.export_text')}</p>
        <div class="row"><input class="input" id="exurl" readonly value="${ical.export_url || ''}" style="flex:1;min-width:0" translate="no" aria-label="${t('hcal.export_title')}">
          <button class="btn btn-primary btn-sm" type="button" id="excopy">${t('hcal.copy')}</button></div>
        <details class="small"><summary style="cursor:pointer;font-weight:600">${t('hcal.howto_phone')}</summary><ol class="small muted"><li>${t('hcal.howto_phone_1')}</li><li>${t('hcal.howto_phone_2')}</li><li>${t('hcal.howto_phone_3')}</li></ol></details>
        <details class="small"><summary style="cursor:pointer;font-weight:600">${t('hcal.howto_ota')}</summary><p class="small muted">${t('hcal.howto_ota_text')}</p></details>
        <div><button class="btn btn-ghost btn-sm" type="button" id="regen">${t('hcal.regen')}</button></div>
      </div>
      <div class="stack" style="border-top:1px solid var(--garis);padding-top:16px">
        <h3 style="margin:0">${t('hcal.import_title')}</h3>
        <p class="small muted" style="margin:0">${t('hcal.import_text')}</p>
        ${ical.calendars.length ? html`<div class="list">${ical.calendars.map((c) => html`<div class="item" style="align-items:flex-start">
          <span class="grow"><b data-ugc>${c.name}</b>
            <div class="tiny muted" style="overflow-wrap:anywhere" translate="no">${c.url}</div>
            <div class="small">${c.last_status === 'OK' ? html`<span class="status ok">${t('hcal.sync_ok', { n: c.nights })}</span>` : c.last_status === 'ERROR' ? html`<span class="status bad">${t('hcal.sync_error')}</span>` : html`<span class="status">${t('hcal.sync_pending')}</span>`}
              ${c.last_synced_at ? html` <span class="tiny muted">${t('hcal.last_sync', { when: fmtTs(c.last_synced_at) })}</span>` : ''}</div>
            ${c.last_error ? html`<div class="tiny errtext" translate="no">${c.last_error}</div>` : ''}
            ${c.conflicts.length ? html`<div class="small errtext">${t('hcal.conflicts', { dates: c.conflicts.map(fmtDateShort).join(', ') })}</div>` : ''}</span>
          <button class="btn btn-ghost btn-sm" data-rm="${c.id}">${t('hcal.remove')}</button></div>`)}</div>
          <div><button class="btn btn-ghost btn-sm" type="button" id="syncnow">${t('hcal.sync_now')}</button></div>` : ''}
        <form id="addcal" class="form-grid two">
          <label class="field"><span>${t('hcal.cal_name')}</span><input class="input" name="name" required minlength="2" maxlength="40" placeholder="${t('hcal.cal_name_ph')}"></label>
          <label class="field"><span>${t('hcal.cal_url')}</span><input class="input" name="url" required minlength="12" placeholder="https://…/calendar.ics" inputmode="url" translate="no"></label>
          <div class="span2"><button class="btn btn-ghost">${icon('calendar')}${t('hcal.cal_add')}</button></div>
        </form>
      </div>
    </section>`);

  const reload = () => render(main, {}, query);
  body.querySelector('#prop').onchange = (e) => navigate(`/host/calendar?property=${e.target.value}`);

  // Tempahan luar
  const ext = body.querySelector('#ext');
  ext.querySelector('#eci-v').addEventListener('change', (e) => {
    if (!e.target.value) return;
    const wrap = ext.querySelector('#eco').parentElement;
    wrap.dataset.min = addDays(e.target.value, 1);
    const co = ext.querySelector('#eco-v');
    if (!co.value || co.value <= e.target.value) setDateField(wrap, addDays(e.target.value, 1));
  });
  ext.onsubmit = (e) => {
    e.preventDefault();
    const f = Object.fromEntries(new FormData(ext).entries());
    if (!f.check_in || !f.check_out) { toast(t('hcal.ext_dates_needed'), { error: true }); return; }
    withBusy(e.submitter, async () => {
      const r = await post(`/api/host/properties/${pid}/external-bookings`, { check_in: f.check_in, check_out: f.check_out, note: f.note || undefined });
      toast(t('hcal.ext_saved', { n: r.added.length }));
      reload();
    });
  };
  body.querySelectorAll('[data-free]').forEach((b) => b.addEventListener('click', async () => {
    const [from, to] = b.dataset.free.split('|');
    if (!(await confirmDialog(t('hcal.free_title'), t('hcal.free_text', { from: fmtDateShort(from), to: fmtDateShort(to) })))) return;
    const dates = [];
    for (let d = from; d < to; d = addDays(d, 1)) dates.push(d);
    try { await post(`/api/host/properties/${pid}/calendar`, { dates, action: 'unblock' }); toast(t('hcal.freed')); reload(); } catch (err) { showError(err); }
  }));

  // Kalendar pilih-banyak (sekat/buka manual)
  const selEl = body.querySelector('#sel');
  const bBtn = body.querySelector('#block');
  const uBtn = body.querySelector('#unblock');
  const c = calendar(body.querySelector('#cal'), {
    mode: 'multi', minDate: today, marks, months: window.innerWidth >= 720 ? 2 : 1,
    onChange(list) {
      picked = list;
      selEl.textContent = list.length ? t('hcal.n_picked', { n: list.length }) : t('hcal.none_picked');
      bBtn.disabled = !list.some((d) => !marks.has(d));
      uBtn.disabled = !list.some((d) => marks.get(d) === 'blocked');
    },
  });
  const act = (action) => (e) => withBusy(e.currentTarget, async () => {
    const r = await post(`/api/host/properties/${pid}/calendar`, { dates: picked, action });
    toast(t(action === 'block' ? 'hcal.blocked_result' : 'hcal.unblocked_result', { n: r.changed.length, skipped: r.skipped.length }));
    c.reset();
    reload();
  });
  bBtn.onclick = act('block');
  uBtn.onclick = act('unblock');

  // Penyegerakan iCal
  body.querySelector('#excopy').onclick = () => copy(body.querySelector('#exurl').value);
  body.querySelector('#regen').onclick = async () => {
    if (!(await confirmDialog(t('hcal.regen'), t('hcal.regen_text')))) return;
    try { await post(`/api/host/properties/${pid}/ical/regenerate`); toast(t('hcal.regen_done')); reload(); } catch (err) { showError(err); }
  };
  body.querySelector('#syncnow')?.addEventListener('click', (e) => withBusy(e.currentTarget, async () => { await post(`/api/host/properties/${pid}/ical/sync`); toast(t('hcal.synced')); reload(); }));
  body.querySelectorAll('[data-rm]').forEach((b) => b.addEventListener('click', async () => {
    if (!(await confirmDialog(t('hcal.remove'), t('hcal.remove_text')))) return;
    try { await del(`/api/host/properties/${pid}/ical/calendars/${b.dataset.rm}`); toast(t('hcal.removed')); reload(); } catch (err) { showError(err); }
  }));
  body.querySelector('#addcal').onsubmit = (e) => {
    e.preventDefault();
    const f = Object.fromEntries(new FormData(e.target).entries());
    withBusy(e.submitter, async () => { await post(`/api/host/properties/${pid}/ical/calendars`, { name: f.name, url: f.url.trim() }); toast(t('hcal.cal_added')); reload(); });
  };
}
